import {useState, useEffect, useRef} from 'react'
import {EditorConfig} from '../../constants'

const INTERACTION_MODES = EditorConfig.INTERACTION_MODES

function shallowEqual(a, b) {
    if (a === b) return true
    if (!a || !b || typeof a !== 'object' || typeof b !== 'object') return false
    const keysA = Object.keys(a)
    const keysB = Object.keys(b)
    if (keysA.length !== keysB.length) return false
    for (const key of keysA) {
        if (!Object.prototype.hasOwnProperty.call(b, key) || a[key] !== b[key]) {
            return false
        }
    }
    return true
}

/** Selector for document slice (order, nodes, selection). Pan/zoom only change viewport so this avoids re-renders when only viewport changes. */
export function selectorDocument(state) {
    return {order: state.order, nodes: state.nodes, selection: state.selection}
}

/**
 * @param {object} runtime - Editor runtime from createEditorRuntime
 * @param {(state: object) => any} [select] - Optional selector. If provided, only the selected slice is stored and compared (shallow). When viewport-only updates don't change the slice, setState is skipped to avoid re-renders.
 * @returns {{ state, dispatch, undo, redo, interaction, ...runtime }}
 */
export function useEditorRuntime(runtime, select) {
    const getInitialState = () => {
        const full = runtime.getState()
        return select ? select(full) : full
    }
    const [state, setState] = useState(getInitialState)
    const sliceRef = useRef(state)

    useEffect(() => {
        if (!runtime) return
        const unsubscribe = runtime.subscribe((newState) => {
            const nextSlice = select ? select(newState) : newState
            const isInteracting =
                select &&
                (runtime.interaction.is(INTERACTION_MODES.DRAG_NODE) ||
                    runtime.interaction.is(INTERACTION_MODES.TRANSFORM))
            if (isInteracting) {
                sliceRef.current = nextSlice
                return
            }
            if (select && shallowEqual(sliceRef.current, nextSlice)) {
                return
            }
            sliceRef.current = nextSlice
            setState(nextSlice)
        })
        return () => {
            unsubscribe?.()
        }
    }, [runtime, select])

    useEffect(() => {
        if (!runtime?.interaction?.subscribe) return
        const unsubscribe = runtime.interaction.subscribe((nextMode, prevMode) => {
            if (nextMode === INTERACTION_MODES.IDLE) {
                setState(sliceRef.current)
            }
        })
        return () => {
            unsubscribe?.()
        }
    }, [runtime])

    return {
        state,
        dispatch: runtime.dispatch,
        undo: runtime.undo,
        redo: runtime.redo,
        interaction: runtime.interaction,
        ...runtime,
    }
}
